import { z } from "zod";

import { hasDeniedUrlScheme } from "../content-safety/unsafe-url";
import { navigableLinkUrls, pageLinkFields } from "./menu-page-link-fields";

export {
  INVALID_MENU_URL_MESSAGE,
  isNavigableMenuUrl,
  looksLikeHostWithPort,
} from "./menu-url";

/**
 * Navigation menus (feature: menu-builder). The stored tree, the behaviour
 * settings, and the shape the storefront receives — in one file, because the
 * builder and the header must agree on all three.
 *
 * AN ITEM STORES ITS OWN LINK. The builder is where a link is decided: picking a
 * tour, a page, or a destination from the catalog panels fills in the label and
 * the address at that moment, and typing a custom link does the same by hand.
 * Nothing resolves anything afterwards — the menu the operator saved is the menu
 * the storefront renders, which is why the stored tree and the public tree below
 * are the same shape and there is no resolver between them.
 *
 * THE TREE IS THREE NAMED LEVELS, NOT A RECURSIVE SCHEMA WITH A DEPTH RULE.
 * That is not a way of enforcing a limit — it is what the header actually is.
 * Each level renders as a different thing:
 *
 *   level 1  an item in the header bar        may be a mega panel
 *   level 2  a dropdown row, or — under a mega item — a column heading
 *   level 3  a link inside a column or dropdown — or a HEADING: a label with
 *            no address that groups the links nested under it
 *   level 4  a link under a level-3 heading
 *
 * Only a heading has a fourth level, and a heading has nothing else to be: it is
 * its own shape (no `url`, a `kind` literal), so "links under a link" stays
 * unrepresentable. A heading is allowed at level 3 under any bar item and in
 * every mega layout: each renderer draws it its own way, so switching a panel's
 * layout never strands one. A fifth level has nothing to render as and is left
 * unrepresentable. `mega` living only on level 1
 * falls out of the same fact: the wide panel replaces the dropdown a bar item
 * would have opened, and nothing nested has a bar to drop from.
 */

/** Long enough for "Africa & Americas", short enough to stay a nav label. */
export const MAX_MENU_LABEL_CHARS = 80;
export const MAX_MENU_NAME_CHARS = 100;
export const MAX_MENU_HANDLE_CHARS = 60;
/** Named so the builder can refuse an over-long address against the field
 *  rather than spending a round trip to be told 422. */
export const MAX_MENU_URL_CHARS = 2048;

/**
 * Ceilings on the tree, because the header is inlined into every route's HTML.
 *
 * Set well above any real navigation rather than at it — a bar of 20 items is
 * already unusable, and a destinations panel of 6 columns × 20 links is a large
 * but legitimate menu at 127 nodes. These refuse the pathological save (an
 * import loop, a script, a paste of the whole catalog), not the ambitious one.
 */
export const MAX_MENU_BAR_ITEMS = 30;
export const MAX_MENU_ITEMS_TOTAL = 500;

/**
 * A menu's permanent identifier, derived from its name at creation and never
 * changed after (AC-01).
 *
 * It exists because a menu is a thing other surfaces point AT — every Nav
 * Menu, Menu and footer Menu column on an applied header or footer
 * (`menuRefSchema`); per-page-type assignments are the remaining follow-up
 * product.md lists. The operator can rename a menu
 * whenever they like, so the name cannot be that pointer. Readable rather than a
 * uuid so the pointer is legible wherever it surfaces.
 */
export const menuHandleSchema = z
  .string()
  .min(1)
  .max(MAX_MENU_HANDLE_CHARS)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Must be lowercase words joined by -");

/**
 * Where the item came from, recorded so the builder knows whose address it is.
 *
 * The seven catalog sources are DYNAMIC: their address was minted from a
 * catalog row's slug, and the builder shows it read-only — an operator who
 * wants a tour's link to change re-slugs the tour. `custom` is the only source
 * whose address the operator typed, and the only one they may edit.
 *
 * Optional because menus saved before this field existed carry no source. A
 * missing source is not "custom": `parseCatalogAddress` recovers tours,
 * destinations and activity types from the address itself, and only a legacy
 * page item is indistinguishable from a hand-typed path.
 */
export const MENU_ITEM_SOURCES = [
  "tour",
  "destination",
  "activity",
  "page",
  "post",
  "author",
  "category",
  "custom",
] as const;

export type MenuItemSource = (typeof MENU_ITEM_SOURCES)[number];

/**
 * How ONE wide panel arranges its columns.
 *
 * 1  headings across, links listed under each
 * 2  the same columns, each link showing the picture of what it points at
 * 3  headings down a sidebar, the selected one's links filling the panel
 *
 * Imagery is not a separate choice: a link draws a thumbnail when the catalog
 * item it points at has a featured image and draws none when it does not, so
 * layouts 2 and 3 need no toggle to switch — the operator's own catalog decides
 * it. Layout 1 stays text-only whatever the items point at.
 */
export const megaLayoutSchema = z.union([
  z.literal(1),
  z.literal(2),
  z.literal(3),
]);

export type MegaLayout = z.infer<typeof megaLayoutSchema>;

const menuItemFields = {
  /**
   * Client-minted and unique within the menu — see the refine on
   * `menuItemsSchema`. Not a uuid: it identifies a node inside one document, and
   * the builder mints one per row the operator adds.
   */
  id: z.string().min(1).max(64),
  /** The operator's wording, whatever the item was picked from. */
  label: z.string().min(1).max(MAX_MENU_LABEL_CHARS),
  /**
   * Where the item points, as the builder assigned it.
   *
   * `min(1)` is what refuses an item with no address (AC-04). The shape is
   * otherwise deliberately loose — this one field carries catalog addresses the
   * builder filled in and whatever the operator typed by hand, and a real
   * navigation legitimately carries mailto:, tel:, and #anchor targets
   * alongside ordinary paths.
   *
   * The one thing it may not carry is a scheme that executes. The stored string
   * becomes an `href` in the storefront header verbatim, on every route, and
   * that header renders in the ISR tree whose CSP allows `'unsafe-inline'` —
   * so a `javascript:` URL here would run in the same origin as checkout. A
   * deny list rather than an allow list, for the mailto:/tel:/#anchor reason
   * above.
   */
  url: z
    .string()
    .min(1)
    .max(MAX_MENU_URL_CHARS)
    .refine((url) => !hasDeniedUrlScheme(url), {
      message:
        "A menu link cannot use the javascript:, vbscript: or data: scheme.",
    }),
  openInNewTab: z.boolean().default(false),
  source: z.enum(MENU_ITEM_SOURCES).optional(),
  /**
   * The built page this link points at (feature: page-addresses).
   *
   * WordPress's model: a page link remembers WHICH page, and its address is
   * worked out when the menu is served, so a rename, a move, or a parent's move
   * never leaves the link on a stale address. `url` stays stored beside it as
   * the address the builder last saw, for the builder's own display.
   *
   * Only on an item whose `source` is `page` — `menuItemsSchema` refuses it
   * anywhere else. Absent on page links saved before this field existed and on
   * the built-in storefront pages, which are not rows.
   */
  pageId: z.uuid().optional(),
  /**
   * Show the linked page's current title instead of `label`.
   *
   * True when the operator picked the page and left the text alone; the builder
   * sets it false the moment they edit the label. Absent means false, so every
   * link saved before this field keeps the text it has. Only meaningful with
   * `pageId`, and refused without one.
   */
  followTitle: z.boolean().optional(),
};

/** A link at the bottom of the tree — level 3, or level 4 under a heading.
 *  No `children`: nothing renders one. */
const menuItemLinkSchema = z.object(menuItemFields).strict();

/**
 * Level 3 heading — a label that groups the links under it ("Two weeks
 * Trek"), in any layout. Not a link: no `url`, no `openInNewTab`, no
 * `source`, so nothing can render it as one.
 *
 * `kind` is what tells it apart from a link, and the only field a link never
 * has. Its own object rather than an optional `url` on the link shape, so every
 * walker that reads `url` has to decide what a heading means to it — the
 * compiler names each one — instead of meeting an empty string.
 */
const menuHeadingSchema = z
  .object({
    id: menuItemFields.id,
    kind: z.literal("heading"),
    label: menuItemFields.label,
    children: z.array(menuItemLinkSchema),
  })
  .strict();

/** Level 3 — a link, or a heading over links. */
const menuItemLevel3Schema = z.union([menuItemLinkSchema, menuHeadingSchema]);

/** Level 2 — a dropdown row, or a mega panel's column heading. */
const menuItemLevel2Schema = z
  .object({
    ...menuItemFields,
    children: z.array(menuItemLevel3Schema),
  })
  .strict();

/**
 * Level 1 — an item in the header bar, and the only level carrying `mega`.
 *
 * `mega` switches what this item's subtree renders as: its direct children
 * become column headings instead of dropdown rows, and their children the
 * columns' links. That is the level-2/level-3 split below it, reused rather than
 * re-encoded — which is why a mega menu needs no extra shape of its own. `mega`
 * on a childless item is not an error; it renders as a plain link.
 */
const menuItemLevel1Schema = z
  .object({
    ...menuItemFields,
    mega: z.boolean().default(false),
    /**
     * This panel's arrangement, chosen beside the `mega` toggle that turns the
     * panel on. Per item because two panels in one bar are two different
     * shapes of content — a destinations grid and a four-link company menu do
     * not want the same layout.
     *
     * Optional, and absent means "whatever the menu's own default is"
     * (`megaLayoutFor` below). Menus saved before this field existed carry the
     * layout in `settings.megaLayout` alone, so they keep rendering exactly as
     * they did until the operator picks a layout on a panel.
     */
    megaLayout: megaLayoutSchema.optional(),
    children: z.array(menuItemLevel2Schema),
  })
  .strict();

export type MenuItemLink = z.infer<typeof menuItemLinkSchema>;
export type MenuHeading = z.infer<typeof menuHeadingSchema>;
export type MenuItemLevel3 = z.infer<typeof menuItemLevel3Schema>;
export type MenuItemLevel2 = z.infer<typeof menuItemLevel2Schema>;
export type MenuItem = z.infer<typeof menuItemLevel1Schema>;

/** A node at any level, for code that walks the tree without caring how deep. */
export type AnyMenuItem = MenuItem | MenuItemLevel2 | MenuItemLevel3;

/** Whether a level-3 node is a heading rather than a link. */
export function isMenuHeading<N extends object>(
  node: N,
): node is Extract<N, { kind: "heading" }> {
  return "kind" in node && node.kind === "heading";
}

const collectIds = (items: readonly AnyMenuItem[], into: string[]): void => {
  for (const item of items) {
    into.push(item.id);
    if ("children" in item) collectIds(item.children, into);
  }
};

/**
 * The stored tree — and, unchanged, the tree the storefront renders.
 *
 * The uniqueness refine is load-bearing, not defensive: the builder keys every
 * draggable row by id, so two nodes sharing one corrupts reordering and nesting
 * silently, for whichever operator happens to hit it.
 *
 * The size cap is load-bearing for a different reason: this tree is inlined
 * into the server HTML of EVERY route, so its size is paid on every page view
 * by every traveller. Counted across every level rather than per level,
 * because what costs the page is the total number of nodes, not how they are
 * distributed — and the walk that counts them is already here for the ids.
 */
const uniqueIds = (
  items: readonly MenuItem[],
  ctx: z.RefinementCtx,
): string[] => {
  const ids: string[] = [];
  collectIds(items, ids);
  if (new Set(ids).size !== ids.length) {
    ctx.addIssue({
      code: "custom",
      message: "Every menu item needs its own id.",
    });
  }
  return ids;
};

/**
 * The tree as the storefront READS it: every shape rule, and neither size cap.
 *
 * The caps are authoring rules, and a read that enforced them could only enforce
 * them by refusing — which costs the agency its whole menu rather than the one
 * item over the line. Shape is different: it decides how a node renders, so a
 * malformed tree really is unservable and stays refused here.
 *
 * This is not a theoretical distinction. `pruneMenuAddresses` promotes a deleted
 * item's children, so a DELETE can grow the bar past `MAX_MENU_BAR_ITEMS` — and
 * with one schema doing both jobs, that turned a cosmetic overflow into a menu
 * that silently vanished from the storefront (the header fell back to its stock
 * links, per `nav.tsx`). Strict on save, forgiving on read: the operator is told
 * to trim, the traveller still gets the menu.
 */
export const menuItemsReadSchema = z
  .array(menuItemLevel1Schema)
  .superRefine((items, ctx) => {
    uniqueIds(items, ctx);
  });

export const menuItemsSchema = z
  .array(menuItemLevel1Schema)
  .max(
    MAX_MENU_BAR_ITEMS,
    `A header bar holds at most ${MAX_MENU_BAR_ITEMS} items.`,
  )
  .superRefine((items, ctx) => {
    pageLinkFields(items, ctx);
    navigableLinkUrls(items, ctx);
    const ids = uniqueIds(items, ctx);
    if (ids.length > MAX_MENU_ITEMS_TOTAL) {
      ctx.addIssue({
        code: "custom",
        message: `A menu holds at most ${MAX_MENU_ITEMS_TOTAL} items in total, including everything nested. This one has ${ids.length}.`,
      });
    }
  });

/**
 * Whether a saved tree is over either authoring cap.
 *
 * The builder needs this because the two schemas now disagree on purpose: a
 * pruned menu can be over the cap, still be served, and still be REFUSED by the
 * save DTO. Without something to say so, the operator opens that menu, presses
 * Save and gets a 422 with nothing pointing at the cause.
 */
export function menuCapOverflow(items: readonly MenuItem[]): {
  barItems: number;
  totalItems: number;
  overBar: boolean;
  overTotal: boolean;
} {
  const ids: string[] = [];
  collectIds(items, ids);
  return {
    barItems: items.length,
    totalItems: ids.length,
    overBar: items.length > MAX_MENU_BAR_ITEMS,
    overTotal: ids.length > MAX_MENU_ITEMS_TOTAL,
  };
}

/**
 * How the menu behaves on the live site.
 *
 * ONE FIELD, AND IT IS A FALLBACK. `megaLayout` here is the layout a panel uses
 * when the panel itself names none — which is every panel in a menu saved
 * before the field moved onto the item. The builder no longer writes it: an
 * operator picks a layout beside the `mega` toggle, on the panel it belongs to.
 * It stays in the contract because removing it would repoint every menu already
 * saved at layout 1.
 *
 * Interaction is not an option — a heading with a dropdown is always a link of
 * its own, and dropdowns always open on hover (and on focus, which is what
 * keeps them keyboard-operable).
 *
 * Unknown keys are dropped rather than rejected, so a row saved while the
 * interaction toggles still existed keeps parsing instead of costing an agency
 * its header.
 */
export const menuSettingsSchema = z.object({
  megaLayout: megaLayoutSchema.default(1),
});
export type MenuSettings = z.infer<typeof menuSettingsSchema>;

export const DEFAULT_MENU_SETTINGS: MenuSettings = menuSettingsSchema.parse({});

/**
 * The layout one panel actually renders with.
 *
 * ONE RULE, BECAUSE TWO SURFACES RENDER IT. The storefront header draws the
 * live panel and the builder's preview draws the same panel from the draft; a
 * fallback resolved slightly differently in either would show the operator a
 * preview of a layout their visitors never see. Same reason
 * `parseCatalogAddress` lives beside this rather than next to a consumer.
 */
export function megaLayoutFor(
  item: { megaLayout?: MegaLayout },
  settings: MenuSettings,
): MegaLayout {
  return item.megaLayout ?? settings.megaLayout;
}

/**
 * The linked catalog item's current featured image — a tour's cover image, a
 * destination's or an activity type's featured image.
 *
 * RESOLVED AT READ, NOT STORED. This field is absent from `menuItemFields`
 * above on purpose: an item stores only where it points, and the image is
 * looked up from that address when the menu is served. So re-covering a tour
 * updates every menu pointing at it, with nothing to re-pick — which is the
 * one place the menu deliberately does NOT follow the "what was saved is what
 * is served" rule that governs `label` and `url`.
 *
 * The lookup is affordable because the storefront's menu read is cached and
 * tag-busted on save, so it runs about once a minute per agency rather than
 * once per request.
 *
 * Null for everything with no catalog row behind it: pages, hand-typed custom
 * links, an address that no longer matches anything, and a catalog row whose
 * image was never uploaded. Null means the link renders as text with NO space
 * reserved for it — a column of empty placeholder boxes reads worse than a
 * column of plain links.
 *
 * No scheme refine, unlike `url`: this value never comes from operator
 * free-text. It is read from the catalog's own image column and becomes an
 * `<img src>`, which does not execute a URL the way an `href` does.
 */
const servedImageUrlSchema = z
  .string()
  .min(1)
  .max(MAX_MENU_URL_CHARS)
  .nullish();

const publicMenuItemLinkSchema = menuItemLinkSchema.extend({
  imageUrl: servedImageUrlSchema,
});

/** A heading has no address, so no image to resolve — only its links do. */
const publicMenuHeadingSchema = menuHeadingSchema.extend({
  children: z.array(publicMenuItemLinkSchema),
});

const publicMenuItemLevel3Schema = z.union([
  publicMenuItemLinkSchema,
  publicMenuHeadingSchema,
]);

const publicMenuItemLevel2Schema = menuItemLevel2Schema.extend({
  imageUrl: servedImageUrlSchema,
  children: z.array(publicMenuItemLevel3Schema),
});

const publicMenuItemLevel1Schema = menuItemLevel1Schema.extend({
  imageUrl: servedImageUrlSchema,
  children: z.array(publicMenuItemLevel2Schema),
});

export type PublicMenuItemLink = z.infer<typeof publicMenuItemLinkSchema>;
export type PublicMenuHeading = z.infer<typeof publicMenuHeadingSchema>;
export type PublicMenuItemLevel3 = z.infer<typeof publicMenuItemLevel3Schema>;
export type PublicMenuItemLevel2 = z.infer<typeof publicMenuItemLevel2Schema>;
export type PublicMenuItem = z.infer<typeof publicMenuItemLevel1Schema>;

/**
 * The layout-3 sidebar column shown at rest: the first one with links. A panel
 * that reaches the sidebar is not flat (`isFlatMegaPanel`), so one exists. The
 * storefront and the builder's preview both call this.
 */
export function megaPanelRestingColumn<
  C extends { children: readonly unknown[] },
>(children: readonly C[]): C | undefined {
  return children.find((column) => column.children.length > 0);
}

/**
 * Whether a mega panel's children ARE its links rather than its column
 * headings. Every level-2 child is a link the operator added, so no layout
 * drops one — a child with no children of its own still renders, as a column
 * heading with nothing under it (layouts 1 and 2) or a plain sidebar link
 * (layout 3). Dropping them hid every sibling the moment one child gained a
 * level 3 (TC-65 #2).
 *
 * All three layouts have to read this the same way, and none may skip it.
 * Layouts 1 and 2 draw one flat list here. Layout 3 has no sidebar to build
 * either, because a sidebar of headings needs columns to open onto: asking it
 * for them anyway is what left its panel blank, one entry per link and nothing
 * beside them.
 */
export function isFlatMegaPanel(
  children: readonly { children: readonly unknown[] }[],
): boolean {
  return children.every((child) => child.children.length === 0);
}

/** One run of a mega panel's links: under a heading, or loose (`heading` null). */
export interface MenuLinkGroup<L> {
  heading: { id: string; label: string } | null;
  links: L[];
}

type HeadingOf<L> = {
  id: string;
  kind: "heading";
  label: string;
  children: readonly L[];
};

/**
 * A level-3 list as the groups every renderer draws: each heading with its
 * links, and loose links between headings gathered into untitled runs, all in
 * the operator's order. A list with no heading comes back as one untitled run.
 *
 * Shared because the storefront header and the builder's preview must draw the
 * same groups — one rule, not two kept in step.
 */
export function menuLinkGroups<L extends object>(
  nodes: readonly (L | HeadingOf<L>)[],
): MenuLinkGroup<L>[] {
  const groups: MenuLinkGroup<L>[] = [];
  for (const node of nodes) {
    if (isMenuHeading(node)) {
      const heading = node as HeadingOf<L>;
      groups.push({
        heading: { id: heading.id, label: heading.label },
        links: [...heading.children],
      });
      continue;
    }
    const last = groups.at(-1);
    if (last && last.heading === null) last.links.push(node as L);
    else groups.push({ heading: null, links: [node as L] });
  }
  return groups;
}

/**
 * What the storefront receives.
 *
 * The wrapper is what makes "no menu" an explicit `null` — the agency has no
 * menus at all (AC-03). Without it a caller has to read an empty `items` array
 * as "no menu", which is indistinguishable from a menu the operator has
 * emptied, and the two want opposite headers: the standard
 * navigation in the first case, an empty bar in the second.
 *
 * `items` carries the resolved `imageUrl`, and needs none of the size or
 * id-uniqueness refines `menuItemsSchema` applies: this tree is built from one
 * that already passed them.
 */
export const publicMenuSchema = z.object({
  handle: menuHandleSchema,
  items: z.array(publicMenuItemLevel1Schema),
  settings: menuSettingsSchema,
});
export type PublicMenu = z.infer<typeof publicMenuSchema>;

export const menuResponseSchema = z.object({
  menu: publicMenuSchema.nullable(),
});
export type MenuResponse = z.infer<typeof menuResponseSchema>;

/**
 * Every menu the agency has, for a surface that points at menus BY HANDLE
 * rather than taking the first one — today the Header & Footer Builder's Nav
 * Menu and Menu elements (`menuRefSchema`).
 *
 * All of them in one response, not one request per referenced handle, because
 * the handles are only known after the applied header/footer document has been
 * read: fetching per handle would put that read in front of every menu read and
 * turn one parallel hop into two serial ones on every storefront route. An
 * agency has a handful of menus, and the header document it is fetched
 * alongside is the larger of the two payloads.
 *
 * A menu the operator has emptied is present here with `items: []`. That is
 * distinct from a handle that is ABSENT — a menu since deleted — and the two
 * render differently: an empty menu is an empty element, a missing one falls
 * back to the first menu and then to the standard navigation.
 */
export const menusResponseSchema = z.object({
  menus: z.array(publicMenuSchema),
});
export type MenusResponse = z.infer<typeof menusResponseSchema>;
